import { CommonModule, DatePipe } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, RouterModule } from '@angular/router'

import { CaseDetailComponent } from '../../../abacus/components/case-detail.component'
import { ActionType } from '../../../abacus/enums/action-type.enum'
import { InputType } from '../../../abacus/enums/input-type.enum'
import { ResourceMode } from '../../../abacus/enums/resource-mode.enum'
import { Action } from '../../../abacus/interfaces/actions/action.interface'
import { ResourceDefinition } from '../../../abacus/interfaces/resource-definition.interface'
import { BreadcrumbService } from '../../../abacus/services/breadcrumb.service'
import { FileDownloadService } from '../../../abacus/services/file-download.service'
import { FlashMessageService } from '../../../abacus/services/flash-message.service'
import { ResourceService } from '../../../abacus/services/resource.service'
import { HasPermissionDirective } from '../../../abacus/directives/has-permission.directive'
import { ActionDirective, TruncatePipe } from '../../../abacus/standalone-exports'
import { transparencyReportDefinition } from '../transparency-report.definition'
import { QuestionnaireSection } from '../models/questionnaire.model'
import { QuestionnaireReviewComponent } from '../transparency-report-submit/questionnaire-review/questionnaire-review.component'

@Component({
  selector: 'app-transparency-report-detail',
  templateUrl: './transparency-report-detail.component.html',
  styleUrls: ['./transparency-report-detail.component.scss'],
  imports: [
    CommonModule,
    DatePipe,
    TruncatePipe,
    ActionDirective,
    HasPermissionDirective,
    RouterModule,
    QuestionnaireReviewComponent
  ],
  standalone: true
})
export class TransparencyReportDetailComponent extends CaseDetailComponent implements OnInit {
  definition: ResourceDefinition = transparencyReportDefinition

  deleteAction: Action
  rejectAction: Action

  questionnaire: QuestionnaireSection[] = []
  isDownloadingPdf = false

  private detailBreadcrumbService: BreadcrumbService

  constructor(
    breadcrumbService: BreadcrumbService,
    private detailResourceService: ResourceService,
    private detailFlashMessageService: FlashMessageService,
    private fileDownloadService: FileDownloadService,
    activatedRoute: ActivatedRoute
  ) {
    super(
      breadcrumbService,
      detailResourceService,
      detailFlashMessageService,
      activatedRoute
    )
    this.detailBreadcrumbService = breadcrumbService
  }

  async ngOnInit(): Promise<void> {
    await this.initDetailView()
    await this.loadQuestionnaire(this.item.reportingPeriod)

    this.deleteAction = {
      type: ActionType.Delete,
      delete: {
        itemToDelete: this.item,
        definition: transparencyReportDefinition,
        redirectTo: '/transparency-reports'
      }
    }
    this.rejectAction = {
      type: ActionType.OpenCreateEditModal,
      openCreateEditModal: {
        title: `Reject the submission`,
        helpText: `You can provide a reason for rejecting the submission.`,
        definition: transparencyReportDefinition,
        mode: ResourceMode.Patch,
        item: this.item,
        patchURL: `transparency-reports/${this.item.id}/status`,
        fields: [
          {
            label: 'Rejection comment',
            property: 'rejectReason',
            placeholder: 'Explain why you are rejecting this submission.',
            className: 'is-12',
            inputType: InputType.Textarea
          },
          {
            label: 'Status',
            property: 'status',
            forcedValue: 'rejected',
            inputType: InputType.Select,
            hidden: true
          }
        ]
      }
    }
  }

  async changeStatus(status: string): Promise<void> {
    try {
      await this.detailResourceService.patch(
        `transparency-reports/${this.item.id}/status`,
        { status }
      )
      window.location.reload()
    } catch (err) {
      this.detailFlashMessageService.error(
        `Error: Could not change status to "${status}".`
      )
    }
  }

  override setBreadcrumbs() {
    this.detailBreadcrumbService.breadcrumbLinks.next([
      {
        path: `/${this.definition.path}`,
        label: this.definition.title
      },
      {
        label: `View #${this.item.id}`
      }
    ])
  }

  get statusLabel(): string {
    if (!this.item) return ''
    if (this.item.status === 'published') return 'Published'
    if (this.item.status === 'pending_review') return 'Submitted'
    if (this.item.status === 'draft' && this.item.rejectReason) return 'Rejected'
    if (this.item.status === 'draft') return 'Draft'
    if (this.item.status === 'unchanged') return 'Unchanged'
    return this.item.status
  }

  get isPubliclyVisible(): boolean {
    return this.item?.status === 'published' || this.item?.status === 'unchanged'
  }

  get pdfButtonLabel(): string {
    return this.isPubliclyVisible ? 'Download PDF' : 'Preview PDF'
  }

  async downloadPdf(): Promise<void> {
    this.isDownloadingPdf = true

    const suffix = this.isPubliclyVisible ? '' : '-preview'

    try {
      await this.fileDownloadService.download(
        `transparency-reports/${this.item.id}/view-pdf`,
        `transparency-report-${this.item.id}${suffix}.pdf`
      )
    } catch (err) {
      this.detailFlashMessageService.error(
        'Error: Could not download the PDF for this submission.'
      )
    } finally {
      this.isDownloadingPdf = false
    }
  }

  private async loadQuestionnaire(period: number): Promise<void> {
    try {
      const result = await this.detailResourceService.list(
        'transparency-reports/questionnaire',
        { period }
      )
      this.questionnaire = result as QuestionnaireSection[]
    } catch {}
  }
}
